import React, { useState, useEffect } from "react";
import "./App.css";
import styled from "styled-components";
import {
    MELOGY_BLACK,
} from "./style/colors";
import GlobalStyle from "./style/GlobalStyle";
import { ThemeProvider } from "@mui/material/styles";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import muiTheme from "./style/muiTheme";
import LeftSidebar from "./components/sidebar/LeftSidebar";
import Main from "./components/Main";
import { CreateSongProgressBarContext } from "./contexts/CreateSongProgressBarContext";
import CreateSongProgressBar from "./components/progressbar/CreateSongProgressBar";
import { StyledToastContainer } from "./style/StyledToastContainer";
import { 
    createAndFetchMidi, 
    updateInstrumentSetting,
    updateKeySetting 
} from "./util/AppUtil";
import { showToastInfo } from "./style/ShowToast";
import { majorSlow } from "./style/styleSettings/majorSlow";
import { minorSlow } from "./style/styleSettings/minorSlow";
import { majorFast } from "./style/styleSettings/majorFast";
import { minorFast } from "./style/styleSettings/minorFast";

const AppContainer = styled.div`
    height: calc(var(--vh, 1vh) * 100);
    background-color: ${MELOGY_BLACK};
    display: flex;
`;

function App() {
    const [leftSideBarIsOpen, setLeftSideBarIsOpen] = useState(false);
    const maxNumberOfSongs = 50;
    const numberOfSongsToRemoveToFreeUpSpaceAtOneTime = 10;
    const [styles, setStyles] = useState([
        majorSlow,
        minorSlow,
        majorFast,
        minorFast,
    ]);
    const [selectedStyle, setSelectedStyle] = useState(styles[0]);
    const [selectedSong, setSelectedSong] = useState(null);
    const [progress, setProgress] = useState(0);
    const [songs, setSongs] = useState([]);
    const [isPortrait, setIsPortrait] = useState(
        window.innerHeight >= window.innerWidth
    );
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [selectedInstruments, setSelectedInstruments] = useState(
        selectedStyle ? selectedStyle.filter((item) => item.group === "Instruments") : []
    );
    let songCounter = 1;

    // Load favorites from localStorage on mount
    const [favorites, setFavorites] = useState(() => {
        const stored = localStorage.getItem('melogyFavorites');
        return stored ? new Set(JSON.parse(stored)) : new Set();
    });

    // Save favorites to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('melogyFavorites', JSON.stringify(Array.from(favorites)));
    }, [favorites]);

    const toggleFavorite = (songName) => {
        setFavorites(prev => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(songName)) {
                newFavorites.delete(songName);
            } else {
                newFavorites.add(songName);
            }
            return newFavorites;
        });
    };

    const isFavorite = (songName) => {
        return favorites.has(songName);
    };

    useEffect(() => {
        if (!selectedStyle) return;
        const newInstruments = selectedStyle.filter(
            (item) => item.group === "Instruments"
        );
        setSelectedInstruments((prev) =>
            JSON.stringify(newInstruments) === JSON.stringify(prev)
                ? prev
                : newInstruments
        );
    }, [selectedStyle]);

    useEffect(() => {
        const updateOrientationAndHeight = () => {
            const newIsPortrait = window.innerHeight >= window.innerWidth;
            setIsPortrait(newIsPortrait);
            setViewportHeight(window.innerHeight);
            
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };
    
        updateOrientationAndHeight();
    
        window.addEventListener('resize', updateOrientationAndHeight);
        window.addEventListener('orientationchange', updateOrientationAndHeight);
    
        return () => {
            window.removeEventListener('resize', updateOrientationAndHeight);
            window.removeEventListener('orientationchange', updateOrientationAndHeight);
        };
    }, []);

    const handleCreateAndFetchMidi = (styleOverride = null) => {
        const styleToUse = styleOverride || selectedStyle || styles[0];
        createAndFetchMidi(setProgress, setSong, styleToUse);
    };

    const setSong = (midi, style) => {
        if (!midi) return;
        let name = `Song ${songCounter}`;
        while (!isNameUnique(name)) {
            songCounter++;
            name = `Song ${songCounter}`;
        }
        const newSong = { name: name, midi: midi, style: style };
        
        // Ensure new song is not in favorites (remove if it exists from old localStorage data)
        setFavorites(prev => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(name)) {
                newFavorites.delete(name);
            }
            return newFavorites;
        });
        
        setSelectedSong(newSong);
        notifySongLimit();
        if (songs.length >= maxNumberOfSongs) {
            showToastInfo(
                "Removed the ten oldest songs to make room for new ones.",
                5000
            );
            setSongs((prevSongs) => [
                ...prevSongs.slice(numberOfSongsToRemoveToFreeUpSpaceAtOneTime),
                newSong,
            ]);
        } else {
            setSongs((prevSongs) => [...prevSongs, newSong]);
        }
    };

    const notifySongLimit = () => {
        const notifySongLimit =
            songs.length ===
            maxNumberOfSongs - numberOfSongsToRemoveToFreeUpSpaceAtOneTime - 1;
        if (notifySongLimit) {
            showToastInfo(
                `When reaching ${maxNumberOfSongs} songs, the ${numberOfSongsToRemoveToFreeUpSpaceAtOneTime} oldest will be removed to free up space.`,
                5000
            );
        }
    };

    const isNameUnique = (nameToCheck) =>
        !songs.some((song) => song.name === nameToCheck);

    const setSongName = (newName) => {
        const updatedSelectedSong = { ...selectedSong, name: newName };
        setSelectedSong(updatedSelectedSong);
        const updatedSongs = songs.map((song) =>
            song.name === selectedSong.name ? updatedSelectedSong : song
        );
        setSongs(updatedSongs);
    };

    const createSongProgressValue = React.useMemo(
        () => ({
            progress,
            setProgress,
        }),
        [progress]
    );

    const toggleLeftSideBar = () => {
        setLeftSideBarIsOpen(!leftSideBarIsOpen);
    };

    const updateStyle = (settingName, newValue) => {
        const updatedStyle = 
            settingName === "key" ? updateKeySetting(selectedStyle, newValue) : 
            settingName.startsWith("track") ? updateInstrumentSetting(selectedStyle, settingName, newValue) : 
            settingName === "styleSettingsName" ? selectedStyle.map((setting) => {
                if (setting.name !== settingName) {
                    return setting;
                }
                return {
                    ...setting,
                    styleSettingsName: newValue,
                };
            }) :
            selectedStyle.map((setting) => {
                    if (setting.name !== settingName) return setting;
                    if (setting.inputType === "RANGE_SLIDER") {
                        return {
                            ...setting,
                            lowerValue: newValue[0],
                            higherValue: newValue[1],
                        };
                    }
                    return { ...setting, value: newValue };
                });
         
        setSelectedStyle(updatedStyle);
        
        // Get the styleName of the selected style to identify which style to update
        const selectedStyleName = selectedStyle?.find((item) => item.name === "styleName")?.value;
        
        setStyles((prevStyles) =>
            prevStyles.map((style) => {
                const styleName = style?.find((item) => item.name === "styleName")?.value;
                // Only update the style that matches the selected style's name
                return styleName === selectedStyleName
                    ? [...updatedStyle]
                    : style;
            })
        );
    };

    const updateSong = (settingName, newValue) => {
        if (!selectedSong || !selectedSong.style) return;
        
        const updatedStyle = 
            settingName.startsWith("track") ? updateInstrumentSetting(selectedSong.style, settingName, newValue) : 
            selectedSong.style.map((setting) => {
                    if (setting.name !== settingName) return setting;
                    if (setting.inputType === "RANGE_SLIDER") {
                        return {
                            ...setting,
                            lowerValue: newValue[0],
                            higherValue: newValue[1],
                        };
                    }
                    return { ...setting, value: newValue };
                });
        
        const updatedSong = {
            ...selectedSong,
            style: updatedStyle,
        };
        
        setSelectedSong(updatedSong);
        
        // Update the song in the songs array
        setSongs((prevSongs) =>
            prevSongs.map((song) =>
                song.name === selectedSong.name
                    ? updatedSong
                    : song
            )
        );
    };

    return (
        <>
            <GlobalStyle />
            <ThemeProvider theme={muiTheme}>
                <CreateSongProgressBarContext.Provider value={createSongProgressValue}>
                <CreateSongProgressBar progress={progress} />
                <AppContainer>
                    <StyledToastContainer position={toast.POSITION.TOP_RIGHT} />
                    <LeftSidebar
                        songs={songs}
                        selectedSong={selectedSong}
                        setSelectedSong={setSelectedSong}
                        toggleLeftSideBar={toggleLeftSideBar}
                        isOpen={leftSideBarIsOpen}
                    />
                    <Main
                        songs={songs}
                        setSelectedSong={setSelectedSong}
                        selectedSong={selectedSong}
                        selectedInstruments={selectedInstruments}
                        setSongName={setSongName}
                        isNameUnique={isNameUnique}
                        createAndFetchMidi={handleCreateAndFetchMidi}
                        toggleLeftSideBar={toggleLeftSideBar}
                        styles={styles}
                        selectedStyle={selectedStyle}
                        updateStyle={updateStyle}
                        setSelectedStyle={setSelectedStyle}
                        isPortrait={isPortrait}
                        favorites={favorites}
                        toggleFavorite={toggleFavorite}
                        isFavorite={isFavorite}
                        updateSong={updateSong}
                    />
                </AppContainer>
                </CreateSongProgressBarContext.Provider>
            </ThemeProvider>
        </>
    );
}

export default App;